import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, Loader2, Search, ShieldAlert } from 'lucide-react';
import { Logo } from '../components/Logo';
import { RateSectionView } from '../components/rates/RateSectionView';
import { AcknowledgeForm, loadSavedSignature } from '../components/rates/AcknowledgeForm';
import { API_URL } from '../components/rates/rateUtils';

const matches = (item, q) =>
  `${item.code} ${item.description} ${item.definition}`.toLowerCase().includes(q);

export default function RateCardPage() {
  const { token } = useParams();
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState('loading');
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [signature, setSignature] = useState(() => loadSavedSignature(token));

  useEffect(() => {
    axios.get(`${API_URL}/rate-cards/shared/${token}`)
      .then((res) => { setCard(res.data); setStatus('ready'); document.title = `${res.data.title} | Mighty Link`; })
      .catch(() => setStatus('error'));
  }, [token]);

  const sections = useMemo(() => {
    if (!card) return [];
    const q = query.trim().toLowerCase();
    return card.sections
      .filter((s) => activeSection === 'all' || s.id === activeSection)
      .map((s) => ({ ...s, items: q ? s.items.filter((i) => matches(i, q)) : s.items }))
      .filter((s) => s.items.length > 0);
  }, [card, query, activeSection]);

  if (status === 'loading') {
    return <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center"><Loader2 className="animate-spin text-violet-500" size={40} /></div>;
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex flex-col items-center justify-center text-center px-6" data-testid="rate-card-invalid">
        <ShieldAlert className="text-amber-400 mb-4" size={44} />
        <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Link not available</h1>
        <p className="text-slate-400 max-w-md mb-6">This rate card link is invalid or has been replaced. Please ask your Mighty Link recruiter for a new link.</p>
        <Link to="/" className="text-violet-300 hover:text-violet-200 text-sm font-semibold">Go to Mighty Link</Link>
      </div>
    );
  }

  const totalCodes = card.sections.reduce((n, s) => n + s.items.length, 0);
  const pdfHref = `${API_URL}/rate-cards/shared/${token}/pdf${signature ? `?signature_id=${signature.id}` : ''}`;

  return (
    <div className="own-type-scale min-h-screen bg-[#0A0A0F] text-white" data-testid="rate-card-page">
      <header className="border-b border-slate-800/80 bg-[#0A0A0F]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="min-w-0"><Logo size="small" /></Link>
          <a href={pdfHref} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 transition-colors flex-shrink-0" data-testid="download-rate-card-pdf">
            <Download size={16} /> <span><span className="hidden sm:inline">Download </span>PDF</span>
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-24">
        <p className="font-mono text-xs tracking-[0.25em] text-cyan-400 mb-3 animate-fade-in">1099 RATE CARD</p>
        <h1 className="text-3xl sm:text-5xl font-extrabold leading-tight max-w-3xl" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="rate-card-title">
          {card.title}
        </h1>
        <div className="flex flex-wrap gap-2 mt-5 text-xs">
          {card.region && <span className="px-3 py-1.5 rounded-full border border-violet-500/40 text-violet-200 bg-violet-500/10" data-testid="rate-card-region">{card.region}</span>}
          {card.year && <span className="px-3 py-1.5 rounded-full border border-slate-700 text-slate-300">{card.year}</span>}
          <span className="px-3 py-1.5 rounded-full border border-slate-700 text-slate-400">{totalCodes} billing codes</span>
        </div>

        <div className="sticky top-16 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-4 mt-8 bg-[#0A0A0F]/90 backdrop-blur-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or description (e.g. RSP, Fiber)"
              className="w-full h-11 pl-10 pr-3 rounded-xl bg-[#12121A] border border-slate-800 focus:border-violet-500/60 outline-none text-sm text-white placeholder:text-slate-500"
              data-testid="rate-search-input"
            />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {[{ id: 'all', name: 'All' }, ...card.sections].map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${activeSection === s.id ? 'bg-violet-600 border-violet-500 text-white' : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`}
                data-testid={`rate-filter-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6 mt-2">
          {sections.length === 0 && (
            <p className="text-center text-slate-500 py-12" data-testid="rate-no-results">No codes match "{query}".</p>
          )}
          {sections.map((s) => <RateSectionView key={s.id} section={s} />)}
          {card.acknowledgment && (
            <AcknowledgeForm token={token} text={card.acknowledgment} signature={signature} onSigned={setSignature} />
          )}
        </div>
      </main>
    </div>
  );
}
